import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getBreakdownById, getScenesByBreakdownId } from '../../../../api/breakdown';
import { getElementsBySceneId, getElementsByBreakdownId, getElementRelationsByBreakdownID } from '../../../../api/element';
import { getSchedulesByBreakdownId } from '../../../../api/schedule';
import { BreakdownInfo } from '../../../Home/types';
import { SceneInfo, SceneWithUserNames } from '../../../../types/scene.types';
import { ScheduleInfo } from '../../../../types/schedule.types';
import { sortScenes } from '../Utils/SceneCodeUtils';
import { mergeScenesFromCache } from '../Utils/mergeScenesFromCache';
import { SortConfig } from '../BreakdownMain.types';
import { useDataCache } from '../../../../contexts/BreakdownDataCacheContext';
import { useCurrentUser } from '../../../../contexts/CurrentUserContext';
import { useTabCache } from '../../../../contexts/TabCacheContext';
import {
  parseScenesTableSortForBreakdownId,
  resolveEffectiveSceneId,
} from '../../../../utils/resolveUserLastUsed';
import { useUserPermissions } from '../../../../contexts/UserPermissionsContext';
import { ResourceType, getPermissionsBatchByResourceIDs } from '../../../../api/userPermissions';
import type { BreakdownDataCache } from '../../../../types/cache.types';

const DEFAULT_BREAKDOWN_TABLE_SORT: SortConfig = { key: 'sceneCode', direction: 'asc' };

/** BreakdownDataCache + TabCache 合并后可能出现的 UI 字段（与 BreakdownMain.tsx setTabCache 一致） */
type BreakdownTabMergedFields = {
  sortConfig?: SortConfig;
  /** 分场展示名（与 breakdownInfo.breakdownName 一致） */
  breakdownName?: string;
  originalBreakdownName?: string;
  selectedSceneId?: string;
  schedulesList?: ScheduleInfo[];
  recentlyUpdatedScheduleId?: string | null;
};

type BreakdownPageMergedCache = BreakdownDataCache & BreakdownTabMergedFields;

/**
 * 数据获取和初始化 Hook
 * 统一管理场次数据的获取和初始化逻辑
 */
export const useBreakdownData = (
  breakdownId: string | undefined,
  sortConfig: SortConfig,
  selectedSceneId: string | null,
  sceneRefreshTrigger: number,
  // Setters
  setBreakdownInfo: React.Dispatch<React.SetStateAction<BreakdownInfo | null>>,
  setScenes: React.Dispatch<React.SetStateAction<SceneWithUserNames[]>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setBreakdownName: React.Dispatch<React.SetStateAction<string>>,
  setOriginalBreakdownName: React.Dispatch<React.SetStateAction<string>>,
  setSceneLocationDisplayTexts: React.Dispatch<React.SetStateAction<{ [sceneId: string]: string }>>,
  setSceneSetDisplayTexts: React.Dispatch<React.SetStateAction<{ [sceneId: string]: string }>>,
  setSelectedSceneId: React.Dispatch<React.SetStateAction<string | null>>,
  setSelectedElementIds: React.Dispatch<React.SetStateAction<Set<string>>>,
  lastSelectedSceneIdByBreakdownIdRef: React.MutableRefObject<Map<string, string | null>>,
  // 额外的 setters 用于缓存
  setSortConfig?: React.Dispatch<React.SetStateAction<SortConfig>>,
  // 拍摄计划列表 setter（与 TabCache 中的 schedulesList 一致）
  setSchedulesList?: React.Dispatch<React.SetStateAction<ScheduleInfo[]>>,
  setRecentlyUpdatedScheduleId?: React.Dispatch<React.SetStateAction<string | null>>
) => {
  const { t } = useTranslation();
  const { serverSyncedData, localOnlyData } = useCurrentUser();
  const {
    getBreakdownCache,
    setBreakdownCache,
    subscribeBreakdownCache,
    getElementCache,
    setElementCache,
    subscribeElementCache,
    getElementRelationCache,
    setElementRelationCache,
    subscribeElementRelationCache,
  } = useDataCache();
  const { getCache: getTabCache, setCache: setTabCache, hasCache: hasTabCache } = useTabCache();
  const { setPermissions } = useUserPermissions();
  
  // 使用 ref 跟踪之前的 breakdownId 和 selectedSceneId，用于在切换分场时保存状态
  const prevBreakdownIdRef = useRef<string | undefined>(breakdownId);
  const prevSelectedSceneIdRef = useRef<string | null>(selectedSceneId);
  const isUpdatingFromCacheRef = useRef(false);
  const currentScenesRef = useRef<SceneWithUserNames[]>([]);

  // 同步阶段从完整缓存恢复（含排序），避免 BreakdownMain.tsx 的 Tab 同步 useEffect 先用初始 sceneCode 覆盖 TabCache
  useLayoutEffect(() => {
    if (!breakdownId) {
      setBreakdownInfo(null);
      setScenes([]);
      setLoading(false);
      setError(null);
      return;
    }

    const prevBreakdownId = prevBreakdownIdRef.current;
    const prevSelectedSceneId = prevSelectedSceneIdRef.current;

    if (prevBreakdownId && prevBreakdownId !== breakdownId) {
      currentScenesRef.current = [];
    }

    if (prevBreakdownId && prevBreakdownId !== breakdownId && prevSelectedSceneId) {
      lastSelectedSceneIdByBreakdownIdRef.current.set(prevBreakdownId, prevSelectedSceneId);
      const prevTabId = `breakdownList-${prevBreakdownId}`;
      const prevTabCache = getTabCache(prevTabId);
      if (prevTabCache) {
        setTabCache(prevTabId, prevBreakdownId, `/breakdown/${prevBreakdownId}`, {
          ...prevTabCache,
          selectedSceneId: prevSelectedSceneId,
        });
      }
    }

    prevBreakdownIdRef.current = breakdownId;

    const tabId = `breakdownList-${breakdownId}`;
    let tabCachedData: Record<string, unknown> | null = null;
    if (hasTabCache(tabId)) {
      tabCachedData = getTabCache(tabId) as Record<string, unknown>;
    }

    const breakdownCache = getBreakdownCache(breakdownId);
    const elementCache = getElementCache(breakdownId);
    const elementRelationCache = getElementRelationCache(breakdownId);

    const cachedData: BreakdownPageMergedCache | null = breakdownCache
      ? ({
          ...breakdownCache,
          elements: elementCache?.elements || breakdownCache.elements || [],
          elementRelations:
            elementRelationCache?.elementRelations || breakdownCache.elementRelations || [],
          ...(tabCachedData || {}),
        } as BreakdownPageMergedCache)
      : null;

    const isCacheValid =
      cachedData &&
      cachedData.breakdownInfo != null &&
      Array.isArray(cachedData.scenes) &&
      cachedData.scenes.length >= 0;

    if (!isCacheValid || !cachedData) {
      return;
    }

    setBreakdownInfo(cachedData.breakdownInfo!);
    const restoredRaw = Array.isArray(cachedData.scenes) ? cachedData.scenes : [];
    const fromLocalSort = parseScenesTableSortForBreakdownId(
      localOnlyData.userLastUsedScenesTableSort,
      breakdownId
    );
    // 本地排序记忆优先于 Tab：避免 Tab 同步 effect 曾写入默认 sceneCode 覆盖 userLastUsedScenesTableSort
    const resolvedSort: SortConfig =
      fromLocalSort ?? cachedData.sortConfig ?? DEFAULT_BREAKDOWN_TABLE_SORT;
    const restoredScenes = mergeScenesFromCache(
      restoredRaw,
      currentScenesRef.current,
      resolvedSort
    );
    setScenes(restoredScenes);
    currentScenesRef.current = restoredScenes;

    const restoredLocationTexts: { [sceneId: string]: string } = {};
    const restoredSetTexts: { [sceneId: string]: string } = {};
    restoredScenes.forEach((scene: SceneWithUserNames) => {
      restoredLocationTexts[scene.sceneID] = scene.sceneLocation || '';
      restoredSetTexts[scene.sceneID] = scene.sceneSet || '';
    });
    setSceneLocationDisplayTexts(restoredLocationTexts);
    setSceneSetDisplayTexts(restoredSetTexts);

    setBreakdownName(cachedData.breakdownName || '未命名分场');
    setOriginalBreakdownName(cachedData.originalBreakdownName || cachedData.breakdownName || '未命名分场');

    if (setSortConfig) {
      setSortConfig(resolvedSort);
    }

    setTabCache(tabId, breakdownId, `/breakdown/${breakdownId}`, {
      ...(tabCachedData || {}),
      sortConfig: resolvedSort,
    });

    const list = restoredScenes;
    if (cachedData.selectedSceneId) {
      const isValid = list.some((scene) => scene.sceneID === cachedData.selectedSceneId);
      if (isValid) {
        setSelectedSceneId(cachedData.selectedSceneId);
      } else if (list.length > 0) {
        setSelectedSceneId(
          resolveEffectiveSceneId(serverSyncedData.userLastUsedSceneID, breakdownId, list)
        );
      } else {
        setSelectedSceneId(null);
      }
    } else if (list.length > 0) {
      setSelectedSceneId(
        resolveEffectiveSceneId(serverSyncedData.userLastUsedSceneID, breakdownId, list)
      );
    } else {
      setSelectedSceneId(null);
    }

    if (setSchedulesList && Array.isArray(cachedData.schedulesList)) {
      setSchedulesList(cachedData.schedulesList);
    }

    setLoading(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 与下方 API effect 分工；仅 breakdownId 变化时从缓存重hydrate（避免点击排序后因 Tab 滞后误用旧 tabSort）
  }, [breakdownId]);

  // 缓存未命中时走 API（布局阶段已恢复的不重复请求）
  useEffect(() => {
    const fetchData = async () => {
      if (!breakdownId) {
        return;
      }

      const tabId = `breakdownList-${breakdownId}`;
      let tabCachedData: any = null;
      if (hasTabCache(tabId)) {
        tabCachedData = getTabCache(tabId);
      }

      const breakdownCache = getBreakdownCache(breakdownId);
      const elementCache = getElementCache(breakdownId);
      const elementRelationCache = getElementRelationCache(breakdownId);

      const cachedData: BreakdownPageMergedCache | null = breakdownCache
        ? ({
            ...breakdownCache,
            elements: elementCache?.elements || breakdownCache.elements || [],
            elementRelations:
              elementRelationCache?.elementRelations || breakdownCache.elementRelations || [],
            ...(tabCachedData || {}),
          } as BreakdownPageMergedCache)
        : null;

      const isCacheValid =
        cachedData &&
        cachedData.breakdownInfo != null &&
        Array.isArray(cachedData.scenes) &&
        cachedData.scenes.length >= 0;

      if (isCacheValid) {
        return;
      }

      if (cachedData) {
        console.warn('缓存数据无效，将重新加载数据', { breakdownId, cachedData });
        if (hasTabCache(tabId)) {
          // 注意：这里不清除缓存，让后续加载时覆盖
        }
      }

      setLoading(true);
      setError(null);

      try {
        // 获取分场信息
        const breakdownResponse = await getBreakdownById(breakdownId!);
        setBreakdownInfo(breakdownResponse.data);

        // 初始化分场名称状态
        const newBreakdownName = breakdownResponse.data.breakdownName || '未命名分场';
        setBreakdownName(newBreakdownName);
        setOriginalBreakdownName(newBreakdownName);


        // 获取场次列表
        const scenesResponse = await getScenesByBreakdownId(breakdownId!);
        
        // 确保 data 存在且是数组
        const sceneRowsFromApi = scenesResponse.data || [];
        
        // 直接使用场次数据（不再加载用户姓名信息）
        const scenesWithUserNames: SceneWithUserNames[] = sceneRowsFromApi.map((scene: SceneInfo) => ({ ...scene }));

        const effectiveSort: SortConfig =
          parseScenesTableSortForBreakdownId(localOnlyData.userLastUsedScenesTableSort, breakdownId!) ??
          tabCachedData?.sortConfig ??
          DEFAULT_BREAKDOWN_TABLE_SORT;
        if (setSortConfig) {
          setSortConfig(effectiveSort);
        }

        // 应用排序
        const sortedScenes = sortScenes(scenesWithUserNames, effectiveSort.key, effectiveSort.direction);
        setScenes(sortedScenes);
        currentScenesRef.current = sortedScenes; // 更新 ref，用于后续比较
        
        // 初始化地点显示文本 - 直接使用sceneLocation字段，避免大量API查询
        // 初始化场景地点和场景字段显示文本
        const initialLocationTexts: { [sceneId: string]: string } = {};
        const initialSetTexts: { [sceneId: string]: string } = {};
        sortedScenes.forEach(scene => {
          // 直接使用场次的字段作为显示文本，不进行额外查询
          initialLocationTexts[scene.sceneID] = scene.sceneLocation || '';
          initialSetTexts[scene.sceneID] = scene.sceneSet || '';
        });
        setSceneLocationDisplayTexts(initialLocationTexts);
        setSceneSetDisplayTexts(initialSetTexts);

        // 本分场拍摄计划列表（Schedule）
        let schedulesFetched: ScheduleInfo[] = [];
        try {
          const schedulesResponse = await getSchedulesByBreakdownId(breakdownId!);
          schedulesFetched = schedulesResponse.data || [];
          
          if (setSchedulesList) {
            setSchedulesList(schedulesFetched);
          }
          
          if (schedulesFetched.length > 0) {
            const sortedSchedules = [...schedulesFetched].sort((a, b) => {
              const timeA = a.scheduleUpdateTime ? new Date(a.scheduleUpdateTime).getTime() : 0;
              const timeB = b.scheduleUpdateTime ? new Date(b.scheduleUpdateTime).getTime() : 0;
              return timeB - timeA; // 从近到远排序
            });
            
            const mostRecentSchedule = sortedSchedules[0];
            if (setRecentlyUpdatedScheduleId && mostRecentSchedule?.scheduleID) {
              setRecentlyUpdatedScheduleId(mostRecentSchedule.scheduleID);
            }
          } else {
            // 无拍摄计划时清空最近更新标记
            if (setRecentlyUpdatedScheduleId) {
              setRecentlyUpdatedScheduleId(null);
            }
          }
        } catch (error) {
          // 查询失败时设置为空数组和 null，不影响主流程
          console.warn('查询拍摄计划列表失败:', error);
          if (setSchedulesList) {
            setSchedulesList([]);
          }
          if (setRecentlyUpdatedScheduleId) {
            setRecentlyUpdatedScheduleId(null);
          }
        }
        
        // 自动选中场次：确保进入分场时总是有一个场次被选中
        let finalSelectedSceneId: string | null = null;
        if (sortedScenes.length > 0) {
          // 检查当前选中的场次是否在新分场的场次列表中
          const currentSelectedIsValid = selectedSceneId && sortedScenes.some(scene => scene.sceneID === selectedSceneId);
          
          if (!currentSelectedIsValid) {
            // 如果当前选中的场次无效（不在列表中或为null），尝试恢复该分场之前选中的场次
            const lastSelectedSceneId = breakdownId ? lastSelectedSceneIdByBreakdownIdRef.current.get(breakdownId) : null;
            const lastSelectedIsValid = lastSelectedSceneId && sortedScenes.some(scene => scene.sceneID === lastSelectedSceneId);
            
            if (lastSelectedIsValid) {
              finalSelectedSceneId = lastSelectedSceneId;
            } else {
              finalSelectedSceneId = resolveEffectiveSceneId(
                serverSyncedData.userLastUsedSceneID,
                breakdownId!,
                sortedScenes
              );
            }
          } else {
            // 如果当前选中的场次有效，则保持选中
            finalSelectedSceneId = selectedSceneId;
          }
          
          setSelectedSceneId(finalSelectedSceneId);
        }
        
        // 加载 elements 和 elementRelations
        let elements: any[] = [];
        let elementRelations: any[] = [];
        
        try {
          // 并行加载 elements 和 elementRelations
          const [elementsResponse, relationsResponse] = await Promise.all([
            getElementsByBreakdownId(breakdownId!),
            getElementRelationsByBreakdownID(breakdownId!)
          ]);
          
          elements = elementsResponse.data || [];
          elementRelations = relationsResponse.data || [];
        } catch (error) {
          console.warn('加载场次元素和关系数据失败（不影响主流程）:', error);
          // 失败时使用空数组，不影响主流程
        }
        
        // 保存到 BreakdownDataCacheContext（数据缓存）
        setBreakdownCache(breakdownId!, {
          breakdownInfo: breakdownResponse.data,
          scenes: sortedScenes,
          elements,
          elementRelations,
        });
        
        setElementCache(breakdownId!, {
          elements,
        });
        
        setElementRelationCache(breakdownId!, {
          elementRelations,
        });

        // 获取权限数据（场次不再请求权限；分场、拍摄计划正常拉取）
        try {
          const scheduleIDs = schedulesFetched.map((s) => s.scheduleID).filter((id): id is string => !!id);

          const breakdownPermissionsMap = await getPermissionsBatchByResourceIDs(ResourceType.BREAKDOWN, [breakdownId!]);
          breakdownPermissionsMap.forEach((permissions, resourceID) => {
            setPermissions(ResourceType.BREAKDOWN, resourceID, permissions);
          });

          if (scheduleIDs.length > 0) {
            const schedulesPermissionsMap = await getPermissionsBatchByResourceIDs(ResourceType.SCHEDULE, scheduleIDs);
            schedulesPermissionsMap.forEach((permissions, resourceID) => {
              setPermissions(ResourceType.SCHEDULE, resourceID, permissions);
            });
          }
        } catch (permError) {
          console.warn('获取权限数据失败（不影响主流程）:', permError);
        }
        
        // 保存到 TabCache（UI 状态）
        const tabId2 = `breakdownList-${breakdownId}`;
        const path2 = `/breakdown/${breakdownId}`;
        const tabCacheData = {
          sceneLocationDisplayTexts: initialLocationTexts,
          selectedSceneId: finalSelectedSceneId,
          sortConfig: effectiveSort,
          breakdownName: newBreakdownName,
          originalBreakdownName: newBreakdownName,
        };
        setTabCache(tabId2, breakdownId!, path2, tabCacheData);
      } catch (error) {
        console.error(t('breakdown.getDataFailed'), error);
        setError(error instanceof Error ? error.message : t('breakdown.getDataFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdownId]);

  // 订阅缓存更新，当其他页面（如拍摄计划该日详情页）更新场次数据时，自动刷新
  useEffect(() => {
    if (!breakdownId) return;
    
    const handleBreakdownCacheUpdate = () => {
      // 如果正在更新，跳过以避免无限循环
      if (isUpdatingFromCacheRef.current) return;

      // 表格单元格编辑中跳过缓存更新，避免编辑中的内容被覆盖
      if ((window as any).__scenesTableIsEditing) {
        return;
      }
      
      const breakdownCache = getBreakdownCache(breakdownId);
      const tabId = `breakdownList-${breakdownId}`;
      const tabCachedData = hasTabCache(tabId) ? getTabCache(tabId) : null;
      
      if (breakdownCache) {
        // 验证缓存数据是否有效：只要有 breakdown 数组就可以更新（不强制要求 breakdownInfo）
        if (Array.isArray(breakdownCache.scenes) && breakdownCache.scenes.length >= 0) {
          isUpdatingFromCacheRef.current = true;
          try {
            const currentSortConfig =
              parseScenesTableSortForBreakdownId(localOnlyData.userLastUsedScenesTableSort, breakdownId) ??
              tabCachedData?.sortConfig ??
              DEFAULT_BREAKDOWN_TABLE_SORT;
            
            // 合并缓存场次与本地占位行，并按排序配置排序
            let sortedScenes: SceneWithUserNames[] = [];
            setScenes((prev) => {
              sortedScenes = mergeScenesFromCache(
                breakdownCache.scenes,
                prev,
                currentSortConfig
              );
              currentScenesRef.current = sortedScenes;
              return sortedScenes;
            });
            
            // 更新 breakdownInfo（如果缓存中存在）
            if (breakdownCache.breakdownInfo) {
              setBreakdownInfo(breakdownCache.breakdownInfo);
            }
            
            // 更新场景地点显示文本（总是从最新的 scenes 中提取，确保数据同步）
            const locationTexts: { [sceneId: string]: string } = {};
            sortedScenes.forEach((scene: SceneWithUserNames) => {
              locationTexts[scene.sceneID] = scene.sceneLocation || '';
            });
            setSceneLocationDisplayTexts(locationTexts);
            
            // 更新场景字段显示文本（总是从最新的 scenes 中提取，确保数据同步）
            const setTexts: { [sceneId: string]: string } = {};
            sortedScenes.forEach((scene: SceneWithUserNames) => {
              setTexts[scene.sceneID] = scene.sceneSet || '';
            });
            setSceneSetDisplayTexts(setTexts);
            
            // 更新分场名称（从 TabCache）
            if (tabCachedData?.breakdownName) {
              setBreakdownName(tabCachedData.breakdownName);
            }
            if (tabCachedData?.originalBreakdownName) {
              setOriginalBreakdownName(tabCachedData.originalBreakdownName);
            }
            
            if (setSortConfig) {
              setSortConfig(currentSortConfig);
            }
            
            // 检查并更新选中的场次（从 TabCache）
            if (tabCachedData?.selectedSceneId) {
              const isValid = sortedScenes.some((scene: SceneWithUserNames) => 
                scene.sceneID === tabCachedData.selectedSceneId
              );
              if (isValid) {
                setSelectedSceneId(tabCachedData.selectedSceneId);
              } else if (sortedScenes.length > 0) {
                // 如果当前选中的场次无效，选中第一个
                setSelectedSceneId(sortedScenes[0].sceneID);
              }
            }
          } finally {
            // 使用 setTimeout 确保状态更新完成后再重置标志
            setTimeout(() => {
              isUpdatingFromCacheRef.current = false;
            }, 0);
          }
        }
      }
    };
    
    // 订阅 Breakdown 缓存更新
    const unsubscribeBreakdown = subscribeBreakdownCache(breakdownId, handleBreakdownCacheUpdate);
    
    // 也订阅 Element 和 ElementRelation 缓存更新（虽然主要数据在 Breakdown 缓存中）
    const handleElementCacheUpdate = () => {
      // Element 更新时，可能需要刷新相关 UI
      handleBreakdownCacheUpdate();
    };
    
    const unsubscribeElements = subscribeElementCache(breakdownId, handleElementCacheUpdate);
    const unsubscribeElementRelations = subscribeElementRelationCache(breakdownId, handleElementCacheUpdate);
    
    return () => {
      unsubscribeBreakdown();
      unsubscribeElements();
      unsubscribeElementRelations();
    };
  }, [
    breakdownId,
    localOnlyData.userLastUsedScenesTableSort,
    getBreakdownCache,
    subscribeBreakdownCache,
    subscribeElementCache,
    subscribeElementRelationCache,
    hasTabCache,
    getTabCache,
    setScenes,
    setBreakdownInfo,
    setSceneLocationDisplayTexts,
    setSceneSetDisplayTexts,
    setBreakdownName,
    setOriginalBreakdownName,
    setSelectedSceneId,
    setSortConfig,
  ]);
  
  // 更新 selectedSceneId 的 ref，并记录到当前分场的选中状态中
  useEffect(() => {
    prevSelectedSceneIdRef.current = selectedSceneId;
    // 当用户选中场次时，更新当前分场的选中场次记录
    if (breakdownId && selectedSceneId) {
      lastSelectedSceneIdByBreakdownIdRef.current.set(breakdownId, selectedSceneId);
    }
  }, [breakdownId, selectedSceneId, lastSelectedSceneIdByBreakdownIdRef]);

  // 获取已选择的场次元素ID列表
  useEffect(() => {
    const fetchSelectedElements = async () => {
      if (!selectedSceneId) {
        setSelectedElementIds(new Set());
        return;
      }

      try {
        const elementsResponse = await getElementsBySceneId(selectedSceneId);
        const elements = elementsResponse.data || [];
        const elementIds = new Set<string>(elements.map((item: any) => item.elementID));
        setSelectedElementIds(elementIds);
      } catch (error) {
        console.error('获取已选择的场次元素失败:', error);
        setSelectedElementIds(new Set());
      }
    };

    fetchSelectedElements();
  }, [selectedSceneId, sceneRefreshTrigger, setSelectedElementIds]);
};

